import React, { useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { format } from "date-fns";

import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AddIcon from "@mui/icons-material/Add";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Divider,
  Button,
  Typography,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { makeStyles } from "helpers/StyleHelper";
import { useTheme } from "@mui/material/styles";

import useGlobalContext from "hooks/globalData";
import useDebounce from "hooks/useDebounce";
import HTMLQuestion from "components/HTMLQuestion";
import {
  AdminListRequestOfQuestionFilter,
  ListResponseOfQuestion,
  Question,
} from "services/KnowfullyAdminClient";
import QuestionAndAnswers from "components/QuestionAndAnswers";

const useStyles = makeStyles({
  question: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: "4",
    lineClamp: 4,
    WebkitBoxOrient: "vertical",
  },
});

type QuestionSearchProps = {
  organizationId: string;
  maxResults?: number;
  onAddQuestion: (question: Question) => void;
};

const QuestionSearch = ({
  organizationId,
  maxResults = 10,
  onAddQuestion,
}: QuestionSearchProps) => {
  const { formatMessage: t } = useIntl();
  const { questionService } = useGlobalContext();
  const styles = useStyles(useTheme());
  const [questionForPopup, setQuestionForPopup] = useState<Question | null>(
    null
  );
  const [searchTerm, setSearchTerm] = useState<string>("");
  const trimmedSearchTerm = searchTerm.trim();
  const debouncedSearchTerm = useDebounce(trimmedSearchTerm, 500);

  const { isError, data, error, isFetching } = useQuery<ListResponseOfQuestion>(
    ["questions", organizationId, maxResults, debouncedSearchTerm],
    () =>
      questionService.getQuestions(
        AdminListRequestOfQuestionFilter.fromJS({
          organizationId,
          pageNumber: 0,
          pageSize: maxResults,
          sortField: "createdOn",
          sortDescending: true,
          parameters: {
            searchText: debouncedSearchTerm,
          },
        })
      ),
    {
      enabled: trimmedSearchTerm !== "" && !!organizationId,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

  if (isError) {
    console.log(error);
  }

  const handleOnPopup = (question: Question) => () => {
    setQuestionForPopup(question);
  };

  const handleAddQuestion = (question: Question) => () => {
    setSearchTerm("");
    setQuestionForPopup(null);
    onAddQuestion(question);
  };

  const noResults =
    trimmedSearchTerm !== "" &&
    !isFetching &&
    (data?.totalRecords ?? 0) === 0;

  const hasResults =
    trimmedSearchTerm !== "" && (data?.totalRecords ?? 0) > 0;

  const hasMore = hasResults && data!.totalRecords! === maxResults;

  return (
    <>
      <Box mb={3}>
        <TextField
          fullWidth
          variant="outlined"
          value={searchTerm ?? ""}
          placeholder={t({ id: "evaluationFormQuestionSearchPlaceholder" })}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setSearchTerm("")}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </Box>

      {isFetching && (
        <Box textAlign="center" my={2}>
          <CircularProgress size={30} />
        </Box>
      )}

      {noResults && (
        <Box textAlign="center" my={2}>
          <Typography>
            <T id="noResults" />
          </Typography>
        </Box>
      )}

      {hasResults &&
        data!.data!.map((question) => (
          <React.Fragment key={question.id}>
            <Box py={2} display="flex" alignItems="center">
              <Box width={80} mr={2} textAlign="center">
                {question.createdOn && (
                  <i>{format(question.createdOn!, "P")}</i>
                )}
              </Box>
              <Box mr={2} flexGrow={1} sx={styles.question}>
                <HTMLQuestion question={question} />
              </Box>
              <Box width={30} mr={2} textAlign="center">
                <IconButton onClick={handleOnPopup(question)} size="small">
                  <OpenInNewIcon />
                </IconButton>
              </Box>
              <Box width={30} mr={2} textAlign="center">
                <i>
                  <T
                    id="questionTypeShort"
                    values={{ type: question.questionType?.name }}
                  />
                </i>
              </Box>
              <Box flexShrink={0}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  size="small"
                  onClick={handleAddQuestion(question)}
                >
                  <T id="evaluationFormAddQuestionButton" />
                </Button>
              </Box>
            </Box>
            <Divider />
          </React.Fragment>
        ))}

      {hasMore && (
        <Box textAlign="center" my={2}>
          <Typography>
            <i>
              <T
                id="evaluationFormQuestionSearchMoreResults"
                values={{
                  filtered: data?.totalRecords,
                  total: data?.totalRecords,
                }}
              />
            </i>
          </Typography>
        </Box>
      )}

      <Dialog
        open={!!questionForPopup}
        disableEnforceFocus
        maxWidth="sm"
        fullWidth
      >
        <DialogContent dividers>
          <QuestionAndAnswers question={questionForPopup} />
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            onClick={() => setQuestionForPopup(null)}
            color="primary"
          >
            <T id="closeButton" />
          </Button>
          <Button onClick={handleAddQuestion(questionForPopup!)} color="primary">
            <T id="evaluationFormAddQuestionButton" />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default QuestionSearch;
